import React, { FC, Fragment, useEffect, useState } from 'react';
import { getDatePickerFormat, getDateFnsLocale, formatPatientName } from 'utils/format';
import { Box, Button, Card, CardContent, Divider, FormControl, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import ClearIcon from '@mui/icons-material/Clear';
import { Organization } from 'models/organization';
import { Order } from 'models/order';
import PackagesWithoutOrdersDialog from './packages-without-orders-dialog.component';
import OrderDeliveryDialog from './order-delivery-dialog.component';
import { TransportProvider } from 'models/transport';
import { useLazyQuery } from '@apollo/client';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import { LabClient } from 'models/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';

type NewDeliveryPanelProps = {
    currentClinic: Organization | undefined;
    orders: Order[];
    removeOrder: (orderId: string) => void;
    onClinicChanged: (newClinic: Organization | undefined) => void;
    onShipmentCreate: (clinicId: string, orderIds: string[]) => void;
    onManualPackagesChanged: (noOfPackages: number | undefined) => void;
};
const NewDeliveryPanel: FC<NewDeliveryPanelProps> = ({
    currentClinic,
    orders,
    removeOrder,
    onClinicChanged,
    onShipmentCreate,
    onManualPackagesChanged,
}: NewDeliveryPanelProps) => {
    const { t: translate } = useTranslation(['transport', 'orders']);
    const [deliveryDate, setDeliveryDate] = useState<Date>(new Date());
    const [noOfManualPackages, setNoOfManualPackages] = useState<number>();
    const [isPackagesDialogOpen, setIsPackagesDialogOpen] = useState(false);
    const [isOrderDeliveryDialogOpen, setIsOrderDeliveryDialogOpen] = useState(false);
    const [transportProvider, setTransportProvider] = useState<TransportProvider>({} as TransportProvider);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getLabClientById, { loading: labClientByIdLoading }] = useLazyQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ labClientById: clientSetting }: { labClientById: LabClient }) => {
            setTransportProvider((clientSetting?.transportProvider || {}) as TransportProvider);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (currentClinic) {
            getLabClientById({
                variables: {
                    customerId: currentClinic?._id,
                },
            });
        }
    }, [currentClinic]);

    const handleAddManualPackages = (selectedClinic: Organization, noOfManualPackages: number) => {
        setIsPackagesDialogOpen(false);
        setNoOfManualPackages(noOfManualPackages);
        onManualPackagesChanged(noOfManualPackages);
        if (currentClinic !== selectedClinic) {
            onClinicChanged(selectedClinic);
        }
    };

    const handleRemoveManualPackages = () => {
        setNoOfManualPackages(undefined);
        onManualPackagesChanged(undefined);
    };

    const handleShipmentCreate = (clinicId: string, orderIds: string[]) => {
        onShipmentCreate(clinicId, orderIds);
        onClinicChanged(undefined);
        handleRemoveManualPackages();
        setIsOrderDeliveryDialogOpen(false);
    };

    if (labClientByIdLoading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Card variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box mb={1}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                            <FormControl margin="none" fullWidth>
                                <DatePicker
                                    format={getDatePickerFormat()}
                                    minDate={new Date()}
                                    onChange={(val) => setDeliveryDate(val || new Date())}
                                    label={translate('transport:TRANSPORT_DATE')}
                                    value={deliveryDate}
                                />
                            </FormControl>
                        </LocalizationProvider>
                    </Box>
                    <Box mb={2} display="flex" alignItems="center">
                        <Box flexGrow={1}>
                            {currentClinic ? <Typography variant="body2">{currentClinic.name}</Typography> : undefined}
                        </Box>
                        <Box>
                            {currentClinic ? (
                                <img
                                    style={{ maxHeight: 20 }}
                                    src={`/images/transport-logos/${transportProvider.externalId}.png`}
                                />
                            ) : undefined}
                        </Box>
                    </Box>
                    <Box mb={2}>
                        <Divider />
                    </Box>
                    {noOfManualPackages ? (
                        <Box mb={0.8}>
                            <Card variant="outlined">
                                <Box p={1} display="flex" alignItems="center">
                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                        {noOfManualPackages} {translate('transport:PACKAGE_WITHOUT_ORDER')}
                                    </Typography>
                                    <IconButton aria-label="delete" size="small" onClick={handleRemoveManualPackages}>
                                        <ClearIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            </Card>
                        </Box>
                    ) : undefined}
                    <Box sx={{ maxHeight: '50vh', overflow: 'scroll' }}>
                        {orders.map((order) => (
                            <Box key={order._id} mb={0.8}>
                                <Card variant="outlined">
                                    <Box p={1} display="flex" alignItems="center">
                                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                            {order.orderId} -{' '}
                                            {formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}
                                        </Typography>
                                        <IconButton
                                            aria-label="delete"
                                            size="small"
                                            onClick={() => removeOrder(order._id)}
                                        >
                                            <ClearIcon fontSize="inherit" />
                                        </IconButton>
                                    </Box>
                                </Card>
                            </Box>
                        ))}
                    </Box>
                </CardContent>
                <Divider />
                <Box mt={1} px={1} mb={1}>
                    {!noOfManualPackages ? (
                        <Button
                            size="small"
                            fullWidth
                            startIcon={<AddIcon />}
                            onClick={() => setIsPackagesDialogOpen(true)}
                        >
                            {translate('transport:ADD_PACKAGE_WITHOUT_ORDER')}
                        </Button>
                    ) : undefined}
                </Box>
                <Box px={1} mb={1}>
                    <Button
                        variant="contained"
                        fullWidth
                        color="success"
                        disabled={orders.length === 0 && !noOfManualPackages}
                        onClick={() => setIsOrderDeliveryDialogOpen(true)}
                    >
                        {translate('transport:CREATE_PACKAGE')}
                    </Button>
                </Box>
            </Card>
            <PackagesWithoutOrdersDialog
                open={isPackagesDialogOpen}
                onClose={() => setIsPackagesDialogOpen(false)}
                currentClinic={currentClinic}
                onAddPackages={handleAddManualPackages}
            />
            {currentClinic ? (
                <OrderDeliveryDialog
                    open={isOrderDeliveryDialogOpen}
                    onClose={() => setIsOrderDeliveryDialogOpen(false)}
                    onShipmentCreate={handleShipmentCreate}
                    orders={orders}
                    clinic={currentClinic as Organization}
                    transportDay={deliveryDate}
                    noOfManualPackages={noOfManualPackages}
                />
            ) : undefined}
        </Fragment>
    );
};

export default NewDeliveryPanel;
